import { Component, createRef, useRef } from 'react';
import { debounceTime, delay, mergeMap } from 'rxjs/operators';

import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { DropboxFile } from '../../../models/dropbox-file';
import { GoogleDriveFile } from '../../../models/google-drive-file';
import { Logger } from '../../../models/logger';
import { UploadInProgressFile } from '../../../models/upload-in-progress-file';
import { ProbedData } from '../../../services/file-process.service';
import { ThumbnailService } from '../../../services/thumbnail.service';
import { ConvertOptionCategories } from '../../../types/dropdown.type';
import { InputFileInfo } from '../../../types/file-upload.types';
import { ToType } from '../../../types/types';
import { FileUtils } from '../../../utils/file-utils';
import { untilDestroyed } from '../../../utils/until-destroyed';
import { ConvertOptionsContext } from '../../convert-options/convert-options-context';
import ProgressComponent from '../progress/progress.component';
import styles from './file-card.component.module.scss';
import { ImageDisplayComponent } from './image-display/image-display.component';
import { of } from 'rxjs';
import { OptionalChildrenProps } from '../../../types/react.types';
import Tooltip from '../tooltip/tooltip.component';
import { FileInfoBoxComponent } from './image-display/file-info-box.component';


export interface UpdateProcessProbedDataFnProvider {
    updateProcessProbedDataFn: () => (probeDuration: number) => Promise<any>;
}

interface FileCardComponentProps extends OptionalChildrenProps {
    uploadInProgressFile: UploadInProgressFile<File | GoogleDriveFile | DropboxFile>;
    convertOptionsContext: ConvertOptionsContext;
    convertOptionCategories: ConvertOptionCategories;
    updateProcessProbedDataFnProvider: UpdateProcessProbedDataFnProvider;
    toType: ToType | string;
    fromType: string[],
    removeFile: (data: any) => void;
    orientationChanged: (data: any) => void;
}

interface FileCardComponentState {
    canDisplayThumbnail: boolean;
}


export default class FileCardComponent extends Component<FileCardComponentProps, FileCardComponentState> {
    private readonly logger = Logger.getLogger('FileCardComponent');

    inputFileInfo: InputFileInfo;
    private static inputFileInfoCache: { [key: string]: InputFileInfo } = {};
    probing = false;
    probedData: ProbedData;
    imageCompRef = createRef<ImageDisplayComponent>()

    @lazyInject(IOC_TYPES.ThumbnailService) private thumbnailService: ThumbnailService;

    readonly state: Readonly<FileCardComponentState> = {
        canDisplayThumbnail: false,
    };
    constructor(props: FileCardComponentProps) {
        super(props);
    }

    // TODO change to non async and use then
    componentDidMount() {
        this.props.convertOptionsContext.onSelectedOptionsChanged.pipe(
            untilDestroyed(this),
            debounceTime(100),
        ).subscribe(() => {
            this.forceUpdate();
        });
        this.props.uploadInProgressFile.changes.pipe(
            untilDestroyed(this),
        ).subscribe({
            next: () => {
                this.forceUpdate();
            }
        });

        this.props.uploadInProgressFile.whenComplete().then(() => {
            // TODO Fix for Google Drive file size update
            of({}).pipe(
                delay(FileCardComponent.inputFileInfoCache[this.props.uploadInProgressFile.id] ? 0 : 2000),
                mergeMap(() => {
                    if (FileCardComponent.inputFileInfoCache[this.props.uploadInProgressFile.id]) {
                        return of(FileCardComponent.inputFileInfoCache[this.props.uploadInProgressFile.id]);
                    }
                    return this.thumbnailService.getInputFileInfo(
                        this.props.uploadInProgressFile.getMetadata().getProcessorMetadata(0)
                    );
                }),
            ).subscribe({
                next: (inputFileInfo) => {
                    this.inputFileInfo = inputFileInfo;
                    FileCardComponent.inputFileInfoCache[this.props.uploadInProgressFile.id] = this.inputFileInfo;
                    this.forceUpdate();
                },
                error: (err) => {
                    this.logger.error(err);
                }
            });
        }).catch((err) => {
            console.error(err);
        });

        if (this.props.uploadInProgressFile) {
            // TODO Preact fix possible issue
            const canDisplayThumbnail = FileUtils.isThumbnailSupported(this.props.uploadInProgressFile.getFile().name);
            this.setState({
                canDisplayThumbnail: canDisplayThumbnail,
            });
        }
    }

    async probe(duration) {
        if (!this.probing) {
            this.probing = true;
            if (this.props?.updateProcessProbedDataFnProvider) {
                const probedData = await this.props.updateProcessProbedDataFnProvider.updateProcessProbedDataFn()(duration);
                this.probedData = probedData;
                // const data = await probedData();
                this.probing = false;
                this.logger.trace7({ message: 'User probed file data', data: probedData });
            }
        }
    }

    getHtml() {
        if (true) {
            return <div className={styles['file-card']}>
                <div className={`${this.props.uploadInProgressFile.completed ? 'hidden' : 'shown'}`} style={{
                    position: 'absolute', width: '100%', height: '100%', top: '0',
                    backgroundImage: `linear-gradient(to right, #00f68f54 ${this.props.uploadInProgressFile.getProgress()}%, white ${this.props.uploadInProgressFile.getProgress()}%)`
                }}>
                    <div style={{ display: 'flex', height: '100%', flexDirection: 'row', justifyContent: 'space-evenly', alignItems: 'center' }}>
                        {
                            this.props.uploadInProgressFile.error &&
                            <span className="badge text-bg-primary">Failed to upload!</span>
                        }
                        {
                            !this.props.uploadInProgressFile.error &&
                            <span className="badge text-bg-primary">{this.props.uploadInProgressFile.getProgress()}% Uploaded</span>
                        }

                    </div>
                </div>
                <div className={styles['file-card-image']} >
                    <ImageDisplayComponent key={this.props.uploadInProgressFile.uuid + this.props.uploadInProgressFile.completed}
                        ref={this.imageCompRef}
                        uploadInProgressFile={this.props.uploadInProgressFile}
                        file={this.props.uploadInProgressFile.getFile()}
                        convertOptionCategories={this.props.convertOptionCategories}
                        convertOptionsContext={this.props.convertOptionsContext}
                        probedData={this.probedData}
                        probing={this.probing}
                        toType={this.props.toType}
                        fromType={this.props.fromType}
                        inputFileInfo={this.inputFileInfo}
                        removeFile={$event => this.props.removeFile($event)}
                        orientationChanged={$event => this.props.orientationChanged($event)}
                        probe={$event => this.probe($event)}
                    >
                    </ImageDisplayComponent>
                </div>
                <div className={styles['file-card-body']}>
                    <div className={`${styles['title']} ellipsis ps-1 pt-1`}>
                        <Tooltip text={this.props.uploadInProgressFile.getFile().name}>
                            {this.props.uploadInProgressFile.getFile().name}
                        </Tooltip>
                    </div>
                    {
                        this.inputFileInfo && <div className='ps-1'>
                            <FileInfoBoxComponent
                                fileInfo={this.inputFileInfo}
                                convertOptionsContext={this.props.convertOptionsContext}
                                toType={this.props.toType} fromType={this.props.fromType}></FileInfoBoxComponent>
                        </div>
                    }
                    <div className='d-flex gap-1 not-sort-handler' style={{ position: 'absolute', right: "0", bottom: "0" }}>

                        {
                            !this.imageCompRef.current?.error && this.imageCompRef.current?.shouldShowRotateAndFlip() && <button className={`btn btn-sm btn-secondary ${styles['action-item']}`}
                                onClick={(e) => this.imageCompRef.current?.rotateFileFn(this.imageCompRef.current?.toolsEnabled, e)}>
                                <img src="/icons/svg/arrow-counterclockwise.svg" />
                            </button>
                        }
                        {
                            !this.imageCompRef.current?.error && this.imageCompRef.current?.shouldShowRotateAndFlip() && <button className={`btn btn-sm btn-secondary ${styles['action-item']}`}
                                onClick={(e) => this.imageCompRef.current?.flipFileHFn(this.imageCompRef.current?.toolsEnabled, e)}
                            >
                                <img src="/icons/svg/arrow-left-right.svg" />
                            </button>
                        }
                        {
                            !this.imageCompRef.current?.error && this.imageCompRef.current?.shouldShowRotateAndFlip() && <button className={`btn btn-sm btn-secondary ${styles['action-item']}`}
                                onClick={(e) => this.imageCompRef.current?.flipFileVFn(this.imageCompRef.current?.toolsEnabled, e)}>
                                <img src="/icons/svg/arrow-down-up.svg" />
                            </button>
                        }
                        <button className={`btn btn-sm btn-secondary x-btn-svg ${styles['action-item']} square-edges`} onClick={() => this.props.removeFile(this.props.uploadInProgressFile.getFile())}>
                            <img className='color-red' src="/icons/svg/x.svg" />
                        </button>
                    </div>
                </div>
            </div>
        }


        if (!this.state.canDisplayThumbnail) {
            return <div className={styles['card']}>
                <div className={styles['card-body']} >
                    <div className={styles['close']} onClick={e => this.props.removeFile(this.props.uploadInProgressFile.getFile())}>
                        X
                    </div>
                    <p className={styles['card-title']} >{this.props.uploadInProgressFile.getFile().name}</p>
                </div>
                {
                    <ProgressComponent
                        value={this.props.uploadInProgressFile.getProgress()}
                        postFix='%'
                        preFix={this.props.uploadInProgressFile.getProgress() === 100 ? 'Uploaded' : 'Uploading:'}></ProgressComponent>
                }
            </div>;
        } else {
            return <ImageDisplayComponent key={this.props.uploadInProgressFile.uuid + this.props.uploadInProgressFile.completed}
                uploadInProgressFile={this.props.uploadInProgressFile}
                file={this.props.uploadInProgressFile.getFile()}
                convertOptionCategories={this.props.convertOptionCategories}
                convertOptionsContext={this.props.convertOptionsContext}
                probedData={this.probedData}
                probing={this.probing}
                toType={this.props.toType}
                fromType={this.props.fromType}
                inputFileInfo={this.inputFileInfo}
                removeFile={$event => this.props.removeFile($event)}
                orientationChanged={$event => this.props.orientationChanged($event)}
                probe={$event => this.probe($event)}
            >
            </ImageDisplayComponent>;
        }
    }

    render() {
        return (
            this.getHtml()
        )
    }
}
